import { Controller } from "@hotwired/stimulus"
import { format, formatISO, startOfDay, startOfWeek, endOfWeek, isToday } from 'date-fns';
import { debounce } from "helpers/timing_helpers"

export default class extends Controller {
  static targets = ["dateDisplay", "dateInput", "incrementButton"]
  static values = {
    date: { type: String },
    type: { type: String },
    identifier: { type: String }
  }

  // TODO: i18n! timezones!

  initialize() {
    this.dispatchTimeRangeChangedEvent = debounce(this.dispatchTimeRangeChangedEvent.bind(this), 500)
  }

  connect() {
    // Allows us to prevent triggering valueChanged callbacks before the controller
    // is fully initialized. Not doing so would cause infinite loops.
    this.initialized = false

    this.#toggleIncrementButton()

    // Delay this until the browser has completed the current rendering cycle, and has
    // finished setting up the initial values.
    requestAnimationFrame(() => {
      this.initialized = true
    })
  }

  updateRangeType(event) {
    this.typeValue = event.target.value
    this.dateValue = new Date().toISOString({ representation: 'date' })
  }

  increment() {
    let date = new Date(this.dateValue)

    switch (this.typeValue) {
      case "year":
        date.setFullYear(date.getFullYear() + 1)
        break
      case "quarter":
        date.setMonth(date.getMonth() + 3)
        break
      case "month":
        date.setMonth(date.getMonth() + 1)
        break
      case "week":
        date.setDate(date.getDate() + 7)
        break
      default:
        date.setDate(date.getDate() + 1)
        break
    }

    this.dateValue = date.toISOString({ representation: 'date' })
  }

  decrement() {
    let date = new Date(this.dateValue)

    switch (this.typeValue) {
      case "year":
        date.setFullYear(date.getFullYear() - 1)
        break
      case "quarter":
        date.setMonth(date.getMonth() - 3)
        break
      case "month":
        date.setMonth(date.getMonth() - 1)
        break
      case "week":
        date.setDate(date.getDate() - 7)
        break
      default:
        date.setDate(date.getDate() - 1)
        break
    }

    this.dateValue = date.toISOString({ representation: 'date' })
  }

  dateValueChanged() {
    this.dateDisplayTarget.textContent = this.#displayDate()
    this.dateInputTarget.value = this.dateValue

    this.#toggleIncrementButton()

    if (this.identifierValue && this.initialized) {
      this.dispatchTimeRangeChangedEvent()
    }
  }

  typeValueChanged() {
    // Even though the date doesn't change, we still want to trigger the side effects
    this.dateValueChanged(this.dateValue, this.dateValue)
  }

  dispatchTimeRangeChangedEvent() {
    this.dispatch(
      `${this.identifierValue}:time-range-changed`,
      {
        detail: { date: this.dateValue, type: this.typeValue },
        bubbles: true,
        cancelable: true,
        prefix: false
      }
    )
  }

  #displayDate() {
    let date = new Date(this.dateValue)

    switch (this.typeValue) {
      case "year":
        // 2024
        return format(date, "yyyy")
      case "quarter":
        // Q1 2024
        return format(date, "QQQ yyyy")
      case "month":
        // April 2024
        return format(date, "MMMM yyyy")
      case "week":
        // Week 44, 28 Oct - 3 Nov
        let start = startOfWeek(date, { weekStartsOn: 1 })
        let end = endOfWeek(date, { weekStartsOn: 1 })
        return `Week ${format(date, "w")}, ${format(start, "d MMM")} - ${format(end, "d MMM")}`
      default:
        // Today
        return isToday(date) ? "Today" : format(date, "dd/MM/yyyy")
    }
  }

  #toggleIncrementButton() {
    this.incrementButtonTarget.disabled = startOfDay(new Date(this.dateValue)) >= startOfDay(new Date())
  }
}
